import { useState } from 'react';
import { connect } from 'react-redux';
import { Switch, makeStyles, tokens } from '@fluentui/react-components';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

// design guide
import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideHelpText } from '../../../theme/components/GuideField';

// function component
import * as ComponentFunctions from './MedicalServiceSettingsFormFunctions';

////i18Next
import { useTranslation } from 'react-i18next';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const useStyles = makeStyles({
  setting: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS
  }
});

const MedicalServiceSettingsFormComponent = (
  props: ComponentFunctions.Props
) => {
  const styles = useStyles();
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [, setFormValidationStatus] = useState<
    FormValidation | undefined
  >();

  // form fields state
  const [, setEnablePatientSelfManagement] =
    useState<boolean>(false);
  const [, setServiceProvidedBy3rdParty] =
    useState<boolean>(false);
  const [, setEnableForAppointment] =
    useState<boolean>(false);
  const [, setOnlyForAdministrativeUse] = useState<boolean>(false);

  //useTranslation()
  const [t] = useTranslation();

  // events

  const onChangePatientSelfManagementStatus = (checked: boolean) => {
    setEnablePatientSelfManagement(checked);
    var UpdatedMedicalService = props.MedicalService;
    UpdatedMedicalService.enablePatientSelfManagement = checked;
    SaveMedicalServiceSettings(UpdatedMedicalService);
  };

  const onChangeServiceProvidedBy3rdPartyStatus = (checked: boolean) => {
    setServiceProvidedBy3rdParty(checked);
    var UpdatedMedicalService = props.MedicalService;
    UpdatedMedicalService.serviceProvidedBy3rdParty = checked;
    SaveMedicalServiceSettings(UpdatedMedicalService);
  };

  const onChangeEnableForAppointmentStatus = (checked: boolean) => {
    setEnableForAppointment(checked);
    var UpdatedMedicalService = props.MedicalService;
    UpdatedMedicalService.enableforAppointment = checked;
    SaveMedicalServiceSettings(UpdatedMedicalService);
  };

  const SaveMedicalServiceSettings = (
    MedicalService: AvailableMedicalService
  ) => {
    setSubmittedForm(true);
    props.ChangeMedicalServiceSettings(MedicalService);
  };

  const onChangeOnlyForAdminitrativeUseStatus = (checked: boolean) => {
    setOnlyForAdministrativeUse(checked);
    var UpdatedMedicalService = props.MedicalService;
    UpdatedMedicalService.onlyForAdministrativeUse = checked;
    SaveMedicalServiceSettings(UpdatedMedicalService);
  };

  const ResetForm = () => {
    setEnablePatientSelfManagement(false);
    setServiceProvidedBy3rdParty(false);
    setEnableForAppointment(false);
    setFormValidationStatus(undefined);
    setSubmittedForm(false);
  };

  const HandleCancelForm = () => {
    props.dismissPanel();
    ResetForm();
  };

  // Cada ajuste se guarda al cambiarlo (no hay botón Guardar).
  const formFields = (
    <>
      <div className={styles.setting}>
        <Switch
          label={t('medicalServiceSettingsForm.props.enableAppointment')}
          defaultChecked={props.MedicalService?.enableforAppointment}
          onChange={(_, data) => onChangeEnableForAppointmentStatus(!!data.checked)}
        />
        <GuideHelpText>{t('medicalServiceSettingsForm.props.requestMedicalService')}</GuideHelpText>
      </div>
      <div className={styles.setting}>
        <Switch
          label={t('medicalServiceSettingsForm.props.enablePatientSelfManagement')}
          defaultChecked={props.MedicalService?.enablePatientSelfManagement}
          onChange={(_, data) => onChangePatientSelfManagementStatus(!!data.checked)}
        />
        <GuideHelpText>{t('medicalServiceSettingsForm.props.enableSystem')}</GuideHelpText>
      </div>
      <div className={styles.setting}>
        <Switch
          label={t('medicalServiceSettingsForm.props.serviceProvided')}
          defaultChecked={props.MedicalService?.serviceProvidedBy3rdParty}
          onChange={(_, data) => onChangeServiceProvidedBy3rdPartyStatus(!!data.checked)}
        />
        <GuideHelpText>{t('medicalServiceSettingsForm.props.serviceInformation')}</GuideHelpText>
      </div>
      <div className={styles.setting}>
        <Switch
          label={t('medicalServiceSettingsForm.props.onlyAdministrativeService')}
          defaultChecked={props.MedicalService?.onlyForAdministrativeUse}
          onChange={(_, data) => onChangeOnlyForAdminitrativeUseStatus(!!data.checked)}
        />
        <GuideHelpText>{t('medicalServiceSettingsForm.props.onlyAdministrativeUseInformation')}</GuideHelpText>
      </div>
    </>
  );

  const snomedLink = props.MedicalService
    ? props.MedicalService.isLinkedToSnowmed
      ? `${t('medicalServiceSettingsForm.props.linkedSnowmed')} [${props.MedicalService.snowmedCode}|${props.MedicalService.snowmedName}]`
      : t('medicalServiceSettingsForm.props.notLinkedSnowmed')
    : undefined;

  return (
    <GuideDrawer
      open={props.isOpen}
      size="small"
      eyebrow={props.MedicalService ? t('medicalServiceSettingsForm.props.medicalService') : undefined}
      title={props.MedicalService?.serviceNameReference || t('medicalServiceSettingsForm.props.medicalService')}
      subtitle={snomedLink}
      onDismiss={props.dismissPanel}
      testId="medical-service-settings-drawer"
      secondaryAction={{ label: t('medicalServiceSettingsForm.footer.closeButton'), onClick: HandleCancelForm }}
    >
      {submittedForm &&
        (props.failOnLoading || props.failOnUpdating) &&
        props.error && <ErrorMessageComponent Error={props.error} />}
      {formFields}
    </GuideDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(MedicalServiceSettingsFormComponent as any);
